import React, { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, deleteDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../../firebase';

const AdminMarcas = () => {
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMarca, setNewMarca] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editingLogoFile, setEditingLogoFile] = useState(null);
  const [editingLogoPreview, setEditingLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [newMarcaLogoFile, setNewMarcaLogoFile] = useState(null);
  const [newMarcaLogoPreview, setNewMarcaLogoPreview] = useState(null);

  // Marcas predefinidas para autocompletar
  const marcasPredefinidas = [
    'Toyota', 'Volkswagen', 'Ford', 'Chevrolet', 'Nissan', 'Honda', 'Hyundai',
    'Kia', 'BMW', 'Mercedes-Benz', 'Audi', 'Volkswagen Comerciales', 'Renault',
    'Peugeot', 'Citroën', 'Fiat', 'Jeep', 'Dodge', 'Ram', 'Chrysler',
    'Subaru', 'Mazda', 'Mitsubishi', 'Suzuki', 'Isuzu', 'Daihatsu',
    'Lexus', 'Infiniti', 'Acura', 'Cadillac', 'Buick', 'GMC', 'Lincoln',
    'Volvo', 'Saab', 'Jaguar', 'Land Rover', 'Mini', 'Porsche',
    'Ferrari', 'Lamborghini', 'Maserati', 'Alfa Romeo', 'Lancia',
    'Skoda', 'Seat', 'Opel', 'Vauxhall', 'Holden'
  ];

  const subirLogo = async (file, marcaId) => {
    const ext = file.name.split('.').pop();
    const logoRef = ref(storage, `logos/marcas/${marcaId}.${ext}`);
    await uploadBytes(logoRef, file);
    return await getDownloadURL(logoRef);
  };

  const fetchMarcas = async () => {
    try {
      setLoading(true);
      setError(null);
      const marcasCollection = collection(db, 'marcas');
      const marcasSnapshot = await getDocs(marcasCollection);
      const marcasList = marcasSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Ordenar alfabéticamente
      marcasList.sort((a, b) => a.nombre.localeCompare(b.nombre));
      setMarcas(marcasList);
    } catch (err) {
      console.error('Error al obtener marcas:', err);
      setError('Error al cargar las marcas');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarcas();
  }, []);

  const handleAddMarca = async (e) => {
    e.preventDefault();
    if (!newMarca.trim()) return;

    // Verificar si la marca ya existe
    const marcaExistente = marcas.find(
      marca => marca.nombre.toLowerCase() === newMarca.trim().toLowerCase()
    );

    if (marcaExistente) {
      alert('Esta marca ya existe');
      return;
    }

    try {
      setAdding(true);
      const marcasCollection = collection(db, 'marcas');
      const docRef = await addDoc(marcasCollection, {
        nombre: newMarca.trim(),
        fechaCreacion: new Date(),
        visibleEnDirectorio: true
      });

      let logoUrl = null;
      if (newMarcaLogoFile) {
        logoUrl = await subirLogo(newMarcaLogoFile, docRef.id);
        await updateDoc(doc(db, 'marcas', docRef.id), { logoUrl });
      }

      const nuevaMarca = {
        id: docRef.id,
        nombre: newMarca.trim(),
        logoUrl,
        fechaCreacion: new Date()
      };

      setMarcas(prev => [...prev, nuevaMarca].sort((a, b) => a.nombre.localeCompare(b.nombre)));
      setNewMarca('');
      setNewMarcaLogoFile(null);
      setNewMarcaLogoPreview(null);
      setShowForm(false);
    } catch (err) {
      console.error('Error al agregar marca:', err);
      alert('Error al agregar la marca');
    } finally {
      setAdding(false);
    }
  };

  const handleDeleteMarca = async (id, nombre) => {
    if (!window.confirm(`¿Estás seguro de que deseas eliminar la marca "${nombre}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'marcas', id));
      setMarcas(prev => prev.filter(marca => marca.id !== id));
    } catch (err) {
      console.error('Error al eliminar marca:', err);
      alert('Error al eliminar la marca');
    }
  };

  const handleEditMarca = async (id) => {
    if (!editingName.trim()) return;

    // Verificar si la nueva marca ya existe (excepto la actual)
    const marcaExistente = marcas.find(
      marca => marca.nombre.toLowerCase() === editingName.trim().toLowerCase() && marca.id !== id
    );

    if (marcaExistente) {
      alert('Ya existe una marca con este nombre');
      return;
    }

    try {
      setUploadingLogo(true);
      let logoUrl = marcas.find(m => m.id === id)?.logoUrl || null;

      if (editingLogoFile) {
        logoUrl = await subirLogo(editingLogoFile, id);
      }

      await updateDoc(doc(db, 'marcas', id), {
        nombre: editingName.trim(),
        ...(logoUrl !== undefined && { logoUrl })
      });

      setMarcas(prev =>
        prev.map(marca =>
          marca.id === id
            ? { ...marca, nombre: editingName.trim(), logoUrl }
            : marca
        ).sort((a, b) => a.nombre.localeCompare(b.nombre))
      );

      setEditingId(null);
      setEditingName('');
      setEditingLogoFile(null);
      setEditingLogoPreview(null);
    } catch (err) {
      console.error('Error al actualizar marca:', err);
      alert('Error al actualizar la marca');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleToggleVisible = async (id, valorActual) => {
    try {
      const nuevoValor = !valorActual;
      await updateDoc(doc(db, 'marcas', id), {
        visibleEnDirectorio: nuevoValor
      });
      setMarcas(prev =>
        prev.map(m => m.id === id ? { ...m, visibleEnDirectorio: nuevoValor } : m)
      );
    } catch (err) {
      console.error('Error al cambiar visibilidad:', err);
      alert('Error al actualizar la visibilidad');
    }
  };

  const startEdit = (marca) => {
    setEditingId(marca.id);
    setEditingName(marca.nombre);
    setEditingLogoFile(null);
    setEditingLogoPreview(marca.logoUrl || null);
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditingName('');
  };

  const addMarcasPredefinidas = async () => {
    if (!window.confirm('¿Deseas agregar todas las marcas predefinidas que no existen aún?')) {
      return;
    }

    try {
      setAdding(true);
      const marcasCollection = collection(db, 'marcas');
      const marcasExistentes = marcas.map(m => m.nombre.toLowerCase());
      
      const marcasParaAgregar = marcasPredefinidas.filter(
        marca => !marcasExistentes.includes(marca.toLowerCase())
      );

      const promesas = marcasParaAgregar.map(marca => 
        addDoc(marcasCollection, {
          nombre: marca,
          fechaCreacion: new Date()
        })
      );

      await Promise.all(promesas);
      await fetchMarcas(); // Recargar todas las marcas
      alert(`Se agregaron ${marcasParaAgregar.length} marcas nuevas`);
    } catch (err) {
      console.error('Error al agregar marcas predefinidas:', err);
      alert('Error al agregar las marcas predefinidas');
    } finally {
      setAdding(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Gestión de Marcas</h1>
          <div className="flex gap-2">
            <button
              onClick={() => setShowForm(!showForm)}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {showForm ? 'Cancelar' : 'Nueva Marca'}
            </button>
            {marcas.length === 0 && (
              <button
                onClick={addMarcasPredefinidas}
                disabled={adding}
                className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
              >
                {adding ? 'Agregando...' : 'Agregar Marcas Básicas'}
              </button>
            )}
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
            {error}
          </div>
        )}

        {showForm && (
          <div className="bg-gray-50 p-4 rounded-lg mb-6">
            <form onSubmit={handleAddMarca} className="flex flex-col gap-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newMarca}
                  onChange={(e) => setNewMarca(e.target.value)}
                  placeholder="Nombre de la marca"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  list="marcas-sugeridas"
                  required
                />
                <datalist id="marcas-sugeridas">
                  {marcasPredefinidas
                    .filter(marca =>
                      !marcas.some(m => m.nombre.toLowerCase() === marca.toLowerCase())
                    )
                    .map(marca => (
                      <option key={marca} value={marca} />
                    ))
                  }
                </datalist>
                <button
                  type="submit"
                  disabled={adding}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  {adding ? 'Agregando...' : 'Agregar'}
                </button>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-sm text-gray-600">Logo (opcional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setNewMarcaLogoFile(file);
                    setNewMarcaLogoPreview(URL.createObjectURL(file));
                  }}
                  className="text-sm text-gray-600"
                />
                {newMarcaLogoPreview && (
                  <img src={newMarcaLogoPreview} alt="preview" className="w-10 h-10 object-contain rounded border" />
                )}
              </div>
            </form>
          </div>
        )}

        <div className="mb-4">
          <p className="text-gray-600">
            Total de marcas: <span className="font-semibold">{marcas.length}</span>
          </p>
          <p className="text-xs text-gray-500 mt-1">
            Visibles en área cliente: <span className="font-semibold text-green-700">
              {marcas.filter(m => m.visibleEnDirectorio !== false).length}
            </span>
            {' · '}
            Ocultas: <span className="font-semibold text-gray-500">
              {marcas.filter(m => m.visibleEnDirectorio === false).length}
            </span>
          </p>
          {marcas.length > 0 && (
            <button
              onClick={addMarcasPredefinidas}
              disabled={adding}
              className="mt-2 bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 transition-colors disabled:opacity-50"
            >
              {adding ? 'Agregando...' : 'Agregar Marcas Faltantes'}
            </button>
          )}
        </div>

        <div className="grid gap-2">
          {marcas.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <p>No hay marcas registradas</p>
              <p className="text-sm">Usa el botón "Agregar Marcas Básicas" para comenzar</p>
            </div>
          ) : (
            marcas.map((marca) => (
              <div
                key={marca.id}
                className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                {editingId === marca.id ? (
                  <div className="flex-1 flex flex-col gap-2">
                    <div className="flex gap-2 items-center">
                      {editingLogoPreview && (
                        <img src={editingLogoPreview} alt="logo" className="w-10 h-10 object-contain rounded border bg-gray-50" />
                      )}
                      <input
                        type="text"
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleEditMarca(marca.id);
                          if (e.key === 'Escape') cancelEdit();
                        }}
                        autoFocus
                      />
                    </div>
                    <div className="flex gap-2 items-center flex-wrap">
                      <label className="text-xs text-gray-500 cursor-pointer flex items-center gap-1">
                        <span>📷 Cambiar logo</span>
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            setEditingLogoFile(file);
                            setEditingLogoPreview(URL.createObjectURL(file));
                          }}
                        />
                      </label>
                      <button
                        onClick={() => handleEditMarca(marca.id)}
                        disabled={uploadingLogo}
                        className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50"
                      >
                        {uploadingLogo ? 'Guardando...' : 'Guardar'}
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="bg-gray-600 text-white px-3 py-1 rounded text-sm hover:bg-gray-700"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3">
                      {marca.logoUrl ? (
                        <img src={marca.logoUrl} alt={marca.nombre}
                          className="w-8 h-8 object-contain rounded border bg-gray-50" />
                      ) : (
                        <div className="w-8 h-8 rounded bg-gray-100 flex items-center justify-center text-gray-400 text-xs font-bold border">
                          {marca.nombre.charAt(0)}
                        </div>
                      )}
                      <span className="text-gray-800 font-medium">{marca.nombre}</span>
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="button"
                        onClick={() => handleToggleVisible(marca.id, marca.visibleEnDirectorio !== false)}
                        title={marca.visibleEnDirectorio !== false
                          ? 'Visible en área cliente — clic para ocultar'
                          : 'Oculta en área cliente — clic para mostrar'}
                        className={`flex items-center gap-1.5 px-3 py-1 rounded text-sm font-medium transition-colors ${
                          marca.visibleEnDirectorio !== false
                            ? 'bg-green-100 text-green-700 hover:bg-green-200'
                            : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                        }`}
                      >
                        {marca.visibleEnDirectorio !== false ? (
                          <><span>👁</span> Visible</>
                        ) : (
                          <><span>🚫</span> Oculta</>
                        )}
                      </button>
                      <button
                        onClick={() => startEdit(marca)}
                        className="bg-yellow-600 text-white px-3 py-1 rounded text-sm hover:bg-yellow-700 transition-colors"
                      >
                        Editar
                      </button>
                      <button
                        onClick={() => handleDeleteMarca(marca.id, marca.nombre)}
                        className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition-colors"
                      >
                        Eliminar
                      </button>
                    </div>
                  </>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMarcas;